import { useFeatureFlagEnabled } from 'posthog-js/react';

/**
 * Feature flag name for controlling driver score visibility.
 *
 * When disabled, all driver score components are hidden across the portal:
 * - Driver Score card on the Homepage
 * - Score badge on trip cards (Recent Trips page)
 * - Score badge and Score Breakdown on the Trip Summary page
 */
const DRIVER_SCORE_VISIBILITY_FLAG = 'driver-score-visibility';

/**
 * Hook to check if driver scores should be visible.
 *
 * Controls whether driver score UI elements are rendered across the
 * Customer Portal. When the flag is disabled, scores are hidden but
 * the underlying functionality is preserved — toggling the flag back
 * on restores all score components without a deployment.
 *
 * Defaults to `false` (scores hidden) when the flag has not been
 * configured in PostHog. Turn the flag on to restore score UI
 * without a deployment.
 *
 * Score visibility is not derived from the Telematics APIs.
 *
 * @returns Whether driver score components should be displayed
 *
 * @example
 * ```tsx
 * const showDriverScore = useDriverScoreVisibility();
 *
 * return (
 *   <>
 *     {showDriverScore && <DriverScoreCard policyNumber={policyNumber} />}
 *   </>
 * );
 * ```
 */
export const useDriverScoreVisibility = (): boolean => {
	const isEnabled = useFeatureFlagEnabled(DRIVER_SCORE_VISIBILITY_FLAG);
	return isEnabled ?? false;
};

export default useDriverScoreVisibility;
